import { FilterGroup } from '@fe/core';
import { GDFFrame, GDFIataTurbRecord, GDFPalletCode, GDFPoint } from '../../gdf/type';
import { type WeatherPickingInfoObject } from '../../manager/types';
import { WeatherProductFileInfo } from '../../service/types';
import { WeatherSettings } from '../../settings/settings';
import { WeatherLoaderSource } from '../../weather-loader';
import { WindBarbsResult } from '../store/raster/winds';
import { WeatherStoreProductInfo } from '../store/weather-store';

export const enum MessageType {
    initialized = 'initialized',
    config = 'config',
    loadProduct = 'loadProduct',
    unloadProduct = 'unloadProduct',
    recordProducts = 'recordProducts',
    unloadAll = 'unloadAll',
    getDataGeoJson = 'getDataGeoJson',
    getTooltipData = 'getTooltipData',
    getTooltipDataHotpoly = 'getTooltipDataHotpoly',
    getRasterA = 'getRasterA',
    getRasterTile = 'getRasterTile',
    getHotpolyMesh = 'getHotpolyMesh',
    getLightningPoints = 'getLightningPoints',
    getWindHeatmap = 'getWindHeatmap',
    getWindComposite = 'getWindComposite',
    getTempHeatmap = 'getTempHeatmap',
    getWindBarbsData = 'getWindBarbsData',
    getWindBarbsGeoJson = 'getWindBarbsGeoJson',
    updateSettings = 'updateSettings',
    updateAirportFilterGroup = 'updateAirportFilterGroup',
    updateSuaFilterGroup = 'updateSuaFilterGroup',
    getDisplayDataIataTurb = 'getDisplayDataIataTurb',
    getHoverDataIataTurb = 'getHoverDataIataTurb',
    setLoader = 'setLoader',
    updateMyAirports = 'updateMyAirports',
    // Recording
    setRecordingDir = 'setRecordingDir',
    // For GDF viewer
    getFrame = 'getFrame'
}

export type RequestResponseMessageMap = {
    [MessageType.initialized]: [void, void];
    [MessageType.config]: [{ enableLogging: boolean }, void];
    [MessageType.loadProduct]: [ProductLoadRequest, WeatherStoreProductInfo];
    [MessageType.unloadProduct]: [ProductUnloadRequest, void];
    [MessageType.recordProducts]: [ProductRecordRequest[], void];
    [MessageType.unloadAll]: [void, void];
    [MessageType.setLoader]: [WeatherLoaderSource, void];
    [MessageType.setRecordingDir]: [FileSystemDirectoryHandle | null, void];

    // Get data requests
    [MessageType.getDataGeoJson]: [GetDataGeoJsonRequest, GdfGeoJson];
    [MessageType.getTooltipData]: [TooltipRequest, TooltipResponse];
    [MessageType.getTooltipDataHotpoly]: [TooltipRequestHotpoly, TooltipResponse];
    [MessageType.getFrame]: [GetFrameRequest, GDFFrame | null];
    [MessageType.getRasterA]: [GetRasterARequest, { data: Uint8Array; bounds: number[]; width: number; height: number } | null];
    [MessageType.getHotpolyMesh]: [GetHotpolyMeshRequest, HotpolyMesh[] | null];
    [MessageType.getLightningPoints]: [GetLightningPointsRequest, LightingPoints[] | null];
    [MessageType.getRasterTile]: [TileRequest, ArrayBuffer | null];
    [MessageType.getWindHeatmap]: [GetWindHeatmapRequest, { data: Uint8Array; bounds: number[]; width: number; height: number } | null];
    [MessageType.getTempHeatmap]: [GetTempHeatmapRequest, { data: Uint8Array; bounds: number[]; width: number; height: number } | null];
    [MessageType.getWindComposite]: [GetWindCompositeRequest, { data: Uint8Array; bounds: number[]; width: number; height: number } | null];
    [MessageType.getWindBarbsData]: [GetWindBarbsDataRequest, WindBarbsResult | null];
    [MessageType.getWindBarbsGeoJson]: [GetWindBarbsGeoJsonRequest, any];
    [MessageType.getDisplayDataIataTurb]: [GetDisplayDataIataTurbRequest, GdfIataTurbResponse];

    //
    [MessageType.updateSettings]: [{ workspaceId: number; settings: WeatherSettings }, void];
    [MessageType.updateAirportFilterGroup]: [FilterGroup[], void];
    [MessageType.updateSuaFilterGroup]: [FilterGroup[], void];
    [MessageType.getHoverDataIataTurb]: [IataTurbHoverRequest, IataTurbHoverResponse | null];
    [MessageType.updateMyAirports]: [string[], void];
};

export interface ProductUnloadRequest {
    id: number[];
}

export interface ProductRecordRequest {
    /** 'all' records both current file and previous files */
    frame: 'all' | 'latest';
    product: WeatherProductFileInfo;
}

export interface HotpolyMesh {
    palette: GDFPalletCode;
    vertices: Float32Array;
    indexes: Uint32Array;
    severity: Uint8Array;
}

export interface LightingPoints {
    points: Float32Array;
    iconColor: Uint8Array;
    palette: GDFPalletCode;
}

export interface GdfGeoJson {
    features: GeoJSON.FeatureCollection;
    paletteCode: GDFPalletCode;
}

export interface GdfIataTurbResponse {
    items: Array<{
        position: number[];
        color: number;
        alpha: number;
        id: number;
        rotate: number;
        speed: number | null;
    }>;
}

export interface TooltipRequest {
    productId: number;
    frame: number;
    ids: number[];
}

export type TooltipResponse = WeatherPickingInfoObject[];

export type IataTurbHoverResponse = {
    record: GDFIataTurbRecord;
    id: number;
};

export interface ProductLoadRequest {
    accumulate: boolean;
    product: WeatherProductFileInfo;
}

export type GetDataRequest<T> = T & {
    workspaceId: number;
};

export type TooltipRequestHotpoly = GetDataRequest<{
    id: number;
    position: GDFPoint;
    frame: number;
}>;

export type TileRequest = GetDataRequest<{
    product: number;
    frame: number;
    z: number;
    x: number;
    y: number;
    tileSize: number;
}>;

export type IataTurbHoverRequest = GetDataRequest<{
    id: number;
    frame: number;
    position: number[];
    radiusMeters: number;
}>;

export type GetDataGeoJsonRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetFrameRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetRasterARequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetHotpolyMeshRequest = GetDataRequest<{
    id: number;
    frames: number[];
}>;

export type GetLightningPointsRequest = GetDataRequest<{
    id: number;
    frames: number[];
}>;

export type GetWindHeatmapRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetTempHeatmapRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetWindCompositeRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetWindBarbsDataRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetHotpolyHoverRequest = GetDataRequest<{
    id: number;
    frame: number;
    position: number[];
}>;

export type GetWindBarbsGeoJsonRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;

export type GetDisplayDataIataTurbRequest = GetDataRequest<{
    id: number;
    frame: number;
}>;
